import config from '../config'

export interface ProjectNode {
  id: number
  project_id?: number
  parent_id?: number
  name: string
  type_id: number
  node_type: string
  path: string
  depth: number
  data_path?: string
  description?: string
  created_at: string
  children?: ProjectNode[]
  child_count?: number
  file_count?: number
  // 다른 리전에서 가져온 노드면 리전 코드(us 등). 국내 원생은 없음.
  source_region?: string | null
   // 백엔드에서 내려주는 리소스 단위 권한 정보 (view/manual_upload/auto_upload/download/delete 등)
  actions?: Record<string, boolean>
}

export interface FileItem {
  id: number
  node_id: number
  upload_id?: number
  filename: string
  original_filename?: string
  file_path: string
  file_size: number
  file_type?: string
  thumbnail_url?: string
  created_at: string
  upload_method?: 'manual' | 'auto'
  preprocess_state?: string | null
  sub_type?: string | null
  metadata?: Record<string, any> | null
}

export interface UploadBatchItem {
  id: number
  project_id: number
  node_id: number
  user_id: number
  user_full_name?: string
  source_filename: string
  source_filename_raw?: string
  source_ext?: string
  source_size: number
  status: string
  status_history: any[]
  started_at: string
  completed_at?: string
  total_items: number
  processed_items: number
  failed_items: number
  upload_method?: 'manual' | 'auto'
  trigger_id?: string | null
  trigger_label?: string | null
  trigger_category?: string | null
  trigger_category_label?: string | null
  trigger_condition?: string | null
  trigger_condition_label?: string | null
  trigger_meta?: Record<string, any> | null
  trigger_mode?: string | null
  source_type?: string | null
  sub_type?: string | null
  preprocess_state?: string | null
  // 다운로드 패키징 진행 상태 (다른 사용자가 시작한 것 포함, 서버 job 파일 기준)
  packaging?: { in_progress?: boolean; progress?: number } | null
}

export interface TriggerLabelSchema {
  prefix: string
  categories: Record<
    string,
    {
      label?: string
      display_name?: string
      conditions?: Record<string, string | { label?: string; display_name?: string }> & {
        patterns?: Array<{ match: string; template: string }>
      }
    }
  >
}

export interface ProjectStructure {
  project: { id: number }
  hierarchy: any
  total_nodes: number
  total_files: number
  total_images: number
  nodes: ProjectNode[]
  files: FileItem[]
}

export interface PendingUploadSession {
  key: string
  session_id: string
  project_id: number
  node_id: number
  filename: string
  total_size?: number
  received?: number
}

class RawDataService {
  private baseUrl = config.API_BASE_URL
  private LS_KEY = 'upload_sessions_v1'
  private ABORT_KEY = 'upload_abort_v1'
  // 미완료 업로드 세션 로컬 유지 TTL (기본 24시간)
  private PENDING_TTL_MS = 24 * 60 * 60 * 1000
  private recommendedChunkSize: number | null = null
  private activeUpload: { sessionId: string | null; resumeKey: string | null; abortController: AbortController | null; aborted: boolean } = {
    sessionId: null,
    resumeKey: null,
    abortController: null,
    aborted: false
  }

  // 실시간 진행률 브로드캐스트 (전역 모달 구독용)
  private progressListeners = new Set<(payload: { progress: number; filename: string; batchIndex?: number | null; batchTotal?: number | null }) => void>()
  private triggerLabelsCache: TriggerLabelSchema | null = null
  addProgressListener(listener: (payload: { progress: number; filename: string; batchIndex?: number | null; batchTotal?: number | null }) => void) {
    this.progressListeners.add(listener)
    return () => this.progressListeners.delete(listener)
  }
  private emitProgressUpdate(progress: number, filename: string) {
    let batchIndex: number | null = null
    let batchTotal: number | null = null
    try {
      const totalRaw = localStorage.getItem('upload_batch_total')
      const indexRaw = localStorage.getItem('upload_batch_index')
      const totalNum = totalRaw ? Number(totalRaw) : null
      const indexNum = indexRaw ? Number(indexRaw) : null
      batchTotal = totalNum && !Number.isNaN(totalNum) ? totalNum : null
      batchIndex = indexNum && !Number.isNaN(indexNum) ? indexNum : null
    } catch {}
    this.progressListeners.forEach(fn => {
      try { fn({ progress, filename, batchIndex, batchTotal }) } catch {}
    })
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token')
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }

  // ===== 업로드 세션 관리 =====
  async getUploadSessionStatus(sessionId: string): Promise<{ received: number; total_size: number; filename: string } | null> {
    try {
      const resp = await fetch(`${this.baseUrl}/uploads/session/${sessionId}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      })
      if (!resp.ok) return null
      const data = await resp.json()
      return { received: data.received, total_size: data.total_size, filename: data.filename }
    } catch {
      return null
    }
  }

  async abortUploadSession(sessionId: string, key?: string) {
    await fetch(`${this.baseUrl}/uploads/session/${sessionId}/abort`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    })
    if (key) this.removeStoredSession(key)
  }

  async listPendingSessions(filter?: { projectId?: number; nodeId?: number }): Promise<PendingUploadSession[]> {
    const raw = localStorage.getItem(this.LS_KEY)
    if (!raw) return []
    const map = JSON.parse(raw) as Record<string, { session_id: string; ts?: number }>
    const items: PendingUploadSession[] = []
    for (const [key, val] of Object.entries(map)) {
      const parsed = this.parseResumeKey(key)
      if (!parsed) continue
      if (filter?.projectId != null && parsed.projectId !== filter.projectId) continue
      if (filter?.nodeId != null && parsed.nodeId !== filter.nodeId) continue
      // TTL 검사: 만료된 로컬 세션은 제거
      if (val.ts && (Date.now() - val.ts) > this.PENDING_TTL_MS) {
        this.removeStoredSession(key)
        continue
      }
      const status = await this.getUploadSessionStatus(val.session_id)
      if (!status) {
        // 서버에 세션이 없으면 로컬에서도 정리
        this.removeStoredSession(key)
        continue
      }
      items.push({
        key,
        session_id: val.session_id,
        project_id: parsed.projectId,
        node_id: parsed.nodeId,
        filename: parsed.fileName,
        total_size: status?.total_size,
        received: status?.received
      })
    }
    return items
  }

  // 네트워크 호출 없이, 로컬 스토리지에 저장된 미완료 세션만 반환 (전역 모달용)
  async getTriggerLabels(): Promise<TriggerLabelSchema | null> {
    if (this.triggerLabelsCache) {
      return this.triggerLabelsCache
    }
    try {
      const resp = await fetch(`${this.baseUrl}/uploads/trigger-labels`, {
        headers: this.getAuthHeaders(),
      })
      if (!resp.ok) return null
      const data = await resp.json()
      this.triggerLabelsCache = data
      return data
    } catch {
      return null
    }
  }

  async listPendingSessionsLocal(filter?: { projectId?: number; nodeId?: number }): Promise<PendingUploadSession[]> {
    try {
      const raw = localStorage.getItem(this.LS_KEY)
      if (!raw) return []
      const map = JSON.parse(raw) as Record<string, { session_id: string; ts?: number }>
      const items: PendingUploadSession[] = []
      for (const [key, val] of Object.entries(map)) {
        const parsed = this.parseResumeKey(key)
        if (!parsed) continue
        if (filter?.projectId != null && parsed.projectId !== filter.projectId) continue
        if (filter?.nodeId != null && parsed.nodeId !== filter.nodeId) continue
        // TTL 검사: 만료된 로컬 세션은 제거
        if (val.ts && (Date.now() - val.ts) > this.PENDING_TTL_MS) {
          this.removeStoredSession(key)
          continue
        }
        items.push({
          key,
          session_id: val.session_id,
          project_id: parsed.projectId,
          node_id: parsed.nodeId,
          filename: parsed.fileName,
          // 진행률은 서버 조회 시에만 채움
        })
      }
      return items
    } catch {
      return []
    }
  }

  // 로컬 세션 정리 (만료/형식오류 제거)
  pruneLocalSessions() {
    try {
      const raw = localStorage.getItem(this.LS_KEY)
      if (!raw) return
      const map = JSON.parse(raw) as Record<string, { session_id: string; ts?: number }>
      let changed = false
      for (const [key, val] of Object.entries(map)) {
        if (!val || typeof (val as any).session_id !== 'string') {
          delete map[key]
          changed = true
          continue
        }
        if (val.ts && (Date.now() - val.ts) > this.PENDING_TTL_MS) {
          delete map[key]
          changed = true
        }
      }
      if (changed) localStorage.setItem(this.LS_KEY, JSON.stringify(map))
    } catch {}
  }

  // 🔥 간단한 폴더 트리 API들만 유지

  // 프로젝트 목록 조회
  async getProjects(): Promise<any[]> {
    try {
      const response = await fetch(`${this.baseUrl}/projects/`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      return result.projects || []
    } catch (error) {
      console.error('프로젝트 목록 조회 실패:', error)
      throw error
    }
  }

  // 📁 루트 노드들 조회 (depth=0)
  async getRootNodes(uploadMethod?: 'manual' | 'auto'): Promise<ProjectNode[]> {
    try {
      const params = new URLSearchParams()
      if (uploadMethod) params.set('upload_method', uploadMethod)
      const suffix = params.toString() ? `?${params.toString()}` : ''
      const response = await fetch(`${this.baseUrl}/projects/nodes/root${suffix}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error(`루트 노드 조회 실패: ${response.status}`)
      }

      const result = await response.json()
      const nodes: ProjectNode[] = result
      // 루트 노드들의 child_count/file_count 보강 (빈 ids이면 호출하지 않음)
      if (nodes.length > 0) {
        try {
          const params = new URLSearchParams()
          nodes.forEach(n => params.append('ids', String(n.id)))
          if (uploadMethod) params.set('upload_method', uploadMethod)
          if (Array.from(params.keys()).length > 0) {
            const r = await fetch(`${this.baseUrl}/projects/nodes/counts?${params.toString()}`, {
              headers: this.getAuthHeaders()
            })
            if (r.ok) {
              const counts = await r.json()
              nodes.forEach(n => {
                const c = counts[n.id]
                if (c) {
                  n.child_count = c.child_count
                  n.file_count = c.file_count
                }
              })
            }
          }
        } catch {}
      }
      return nodes
    } catch (error) {
      console.error('루트 노드 조회 실패:', error)
      return []
    }
  }

  // 📁 특정 노드의 자식들 조회
  async getNodeChildren(nodeId: number, uploadMethod?: 'manual' | 'auto'): Promise<ProjectNode[]> {
    try {
      const params = new URLSearchParams()
      if (uploadMethod) params.set('upload_method', uploadMethod)
      const suffix = params.toString() ? `?${params.toString()}` : ''
      const response = await fetch(`${this.baseUrl}/projects/nodes/${nodeId}/children${suffix}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      })

      if (!response.ok) {
        throw new Error(`자식 노드 조회 실패: ${response.status}`)
      }

      const result: ProjectNode[] = await response.json()
      // 자식들의 child_count / file_count가 비어있을 수 있으므로 배치로 보강
      const missingIds = result
        .filter(n => (n.child_count == null && n.file_count == null))
        .map(n => n.id)
      if (missingIds.length > 0) {
        try {
          const params = new URLSearchParams()
          missingIds.forEach(id => params.append('ids', String(id)))
          if (uploadMethod) params.set('upload_method', uploadMethod)
          const r = await fetch(`${this.baseUrl}/projects/nodes/counts?${params.toString()}`, {
            headers: this.getAuthHeaders()
          })
          if (r.ok) {
            const counts = await r.json()
            result.forEach(n => {
              const c = counts[n.id]
              if (c) {
                n.child_count = c.child_count
                n.file_count = c.file_count
              }
            })
          }
        } catch {}
      }
      return result
    } catch (error) {
      console.error('자식 노드 조회 실패:', error)
      return []
    }
  }

  // 📁 노드 정보 조회
  async getNodeById(nodeId: number, uploadMethod?: 'manual' | 'auto'): Promise<ProjectNode | null> {
    try {
      const params = new URLSearchParams()
      if (uploadMethod) params.set('upload_method', uploadMethod)
      const suffix = params.toString() ? `?${params.toString()}` : ''
      const response = await fetch(`${this.baseUrl}/projects/nodes/${nodeId}${suffix}`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      })

      if (!response.ok) {
        if (response.status === 404) {
          return null
        }
        throw new Error(`노드 조회 실패: ${response.status}`)
      }

      const result = await response.json()
      return result
    } catch (error) {
      console.error('노드 조회 실패:', error)
      return null
    }
  }

  // 원천데이터 생성 (프로젝트 + 노드 구조)
  async createRawData(data: {
    projectName: string
    streamGroup?: string
    stream?: string
    projectDescription?: string
    streamGroupDescription?: string
    streamDescription?: string
    existingProjectId?: number
    uploadMode: 'manual' | 'auto'
  }): Promise<any> {
    try {
      // 분기: 기존 프로젝트에 추가 vs 새 프로젝트 생성
      if (data.existingProjectId && data.projectName) {
        // 기존 프로젝트: 경로 기반 노드 생성 (Find-or-Create)
        const parts: string[] = [data.projectName]
        if (data.streamGroup) parts.push(data.streamGroup)
        if (data.stream) parts.push(data.stream)
        const lastType = parts.length >= 3 ? 'STREAM' : (parts.length === 2 ? 'GROUP' : 'PROJECT')

        const resp = await fetch(`${this.baseUrl}/projects/${data.existingProjectId}/nodes/create-by-path`, {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({
            path_parts: parts,
            last_node_type: lastType,
            group_description: data.streamGroupDescription || undefined,
            stream_description: data.streamDescription || undefined
          })
        })
        if (!resp.ok) {
          let errorMessage = '경로 기반 노드 생성에 실패했습니다.'
          try {
            const errorData = await resp.json()
            if (errorData.detail) errorMessage = errorData.detail
          } catch {
            try {
              const text = await resp.text()
              if (text) errorMessage = text
            } catch {}
          }
          throw new Error(errorMessage)
        }
        return await resp.json()
      }

      // 새 프로젝트 생성 (노드 구조 포함)
      const projectResponse = await fetch(`${this.baseUrl}/projects/`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          name: data.projectName || undefined,
          stream_group: data.streamGroup || undefined,
          stream: data.stream || undefined,
          project_description: data.projectDescription || undefined,
          stream_group_description: data.streamGroupDescription || undefined,
          stream_description: data.streamDescription || undefined,
          status: 'active',
          upload_mode: data.uploadMode
        })
      })

      if (!projectResponse.ok) {
        let errorMessage = '프로젝트 생성에 실패했습니다.'
        
        try {
          // JSON 응답인 경우 detail 메시지 추출
          const errorData = await projectResponse.json()
          if (errorData.detail) {
            errorMessage = errorData.detail
          }
        } catch {
          // JSON 파싱 실패 시 텍스트로 시도
          try {
            const errorText = await projectResponse.text()
            if (errorText) {
              errorMessage = errorText
            }
          } catch {
            // 모든 파싱 실패 시 상태 코드 기반 메시지
            if (projectResponse.status === 400 || projectResponse.status === 422) {
              errorMessage = '입력된 정보에 문제가 있습니다. 다시 확인해주세요.'
            } else if (projectResponse.status === 409) {
              errorMessage = '이미 존재하는 원천데이터 구조입니다.'
            }
          }
        }
        
        throw new Error(errorMessage)
      }

      const projectResult = await projectResponse.json()
      // console.log('✅ 프로젝트 및 노드 구조 생성 완료')
      
      return {
        project: projectResult
      }
      
    } catch (error) {
      console.error('원천데이터 생성 실패:', error)
      throw error
    }
  }

  // 노드의 파일 목록 조회 (필터 지원)
  async getNodeFiles(nodeId: number, skip: number = 0, limit: number = 50, uploadMethod?: 'manual' | 'auto', signal?: AbortSignal): Promise<FileItem[]> {
    const params = new URLSearchParams({ skip: String(skip), limit: String(limit) })
    if (uploadMethod) params.set('upload_method', uploadMethod)
    const resp = await fetch(`${this.baseUrl}/files/node/${nodeId}?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
      signal,
    })
    if (!resp.ok) throw new Error(`파일 목록 조회 실패: ${resp.status}`)
    const data = await resp.json()
    return data
  }

  async deleteUploadBatch(batchId: number): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/uploads/history/${batchId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    })
    if (!resp.ok) {
      // 409인 경우, 사용자 친화적 고정 문구로 안내
      if (resp.status === 409) {
        throw new Error('삭제하려는 데이터가 라벨 데이터에 사용되어 삭제가 불가능합니다. 해당 라벨 데이터를 삭제하고 다시 시도해주세요.')
      }
      try {
        const ct = resp.headers.get('content-type') || ''
        if (ct.includes('application/json')) {
          const data = await resp.json()
          const msg = (data && (data.detail || data.message)) || (typeof data === 'string' ? data : '')
          if (msg && String(msg).trim().length > 0) throw new Error(String(msg))
        }
      } catch {}
      try {
        const txt = await resp.text()
        throw new Error(txt || `히스토리 삭제 실패(${resp.status})`)
      } catch {
        throw new Error(`히스토리 삭제 실패(${resp.status})`)
      }
    }
  }

  async listUploadHistory(nodeId: number, skip: number = 0, limit: number = 50, uploadMethod?: 'manual' | 'auto', signal?: AbortSignal): Promise<UploadBatchItem[]> {
    const params = new URLSearchParams({ node_id: String(nodeId), skip: String(skip), limit: String(limit) })
    if (uploadMethod) params.set('upload_method', uploadMethod)
    const resp = await fetch(`${this.baseUrl}/uploads/history?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
      signal,
    })
    if (!resp.ok) throw new Error(`업로드 히스토리 조회 실패: ${resp.status}`)
    return resp.json()
  }

  async countUploadHistory(nodeId: number, uploadMethod?: 'manual' | 'auto'): Promise<number> {
    const params = new URLSearchParams({ node_id: String(nodeId) })
    if (uploadMethod) params.set('upload_method', uploadMethod)
    const resp = await fetch(`${this.baseUrl}/uploads/history/count?${params.toString()}`, {
      headers: this.getAuthHeaders()
    })
    if (!resp.ok) throw new Error(`업로드 히스토리 수 조회 실패: ${resp.status}`)
    const data = await resp.json()
    return data.count ?? 0
  }

  async listUploadHistoryItems(batchId: number, skip: number = 0, limit: number = 50, signal?: AbortSignal): Promise<FileItem[]> {
    const params = new URLSearchParams({ skip: String(skip), limit: String(limit) })
    const resp = await fetch(`${this.baseUrl}/uploads/history/${batchId}/items?${params.toString()}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
      signal,
    })
    if (!resp.ok) throw new Error(`업로드 히스토리 아이템 조회 실패: ${resp.status}`)
    return resp.json()
  }

  async getNodeFilesCount(nodeId: number, uploadMethod?: 'manual' | 'auto', signal?: AbortSignal): Promise<number> {
    const params = new URLSearchParams()
    if (uploadMethod) params.set('upload_method', uploadMethod)
    const suffix = params.toString() ? `?${params.toString()}` : ''
    const resp = await fetch(`${this.baseUrl}/files/node/${nodeId}/count${suffix}`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
      signal,
    })
    if (!resp.ok) throw new Error(`파일 수 조회 실패: ${resp.status}`)
    const data = await resp.json()
    return data.count ?? 0
  }

  // 중복 업로드 체크
  async checkDuplicateUpload(nodeId: number, filename: string): Promise<{
    is_duplicate: boolean
    message: string
    existing_batch_id?: number
    existing_upload_date?: string
  }> {
    const params = new URLSearchParams({
      node_id: String(nodeId),
      filename: filename
    })
    
    const response = await fetch(`${this.baseUrl}/uploads/check-duplicate?${params.toString()}`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    })
    
    if (!response.ok) {
      throw new Error(`중복 체크 실패: ${response.status}`)
    }
    
    return response.json()
  }

  // 파일 업로드 (세션/청크 기반, 이어받기 지원)
  async uploadFile(
    file: File,
    nodeId: number,
    projectId: number,
    onProgress?: (percent: number) => void,
    preprocessModes?: { face: boolean; plate: boolean }
  ): Promise<any> {
    const authHeaders = this.getAuthHeaders()
    // 프런트 체크박스 값을 그대로 사용: 기본값은 비활성(false)
    const facePref = preprocessModes?.face ?? false
    const platePref = preprocessModes?.plate ?? false

    // 세션 키 생성 (파일 식별: 이름+크기+lastModified+노드/프로젝트)
    const resumeKey = this.buildResumeKey(file, nodeId, projectId)

    // 이전 세션에서 남아있을 수 있는 전역 취소 플래그 초기화
    try { localStorage.removeItem(this.ABORT_KEY) } catch {}

    // 1) 기존 세션 조회 및 검증 (있으면 이어서)
    let sessionId: string | null = null
    let offset = 0
    const existing = this.getStoredSession(resumeKey)
    if (existing) {
      try {
        const statusResp = await fetch(`${this.baseUrl}/uploads/session/${existing.session_id}`, {
          method: 'GET',
          headers: authHeaders
        })
        if (statusResp.ok) {
          const status = await statusResp.json()
          // 총 크기/파일명 검증
          if (status.total_size === file.size && status.filename === file.name) {
            sessionId = existing.session_id
            offset = status.received || 0
          } else {
            // 불일치 시 세션 폐기
            this.removeStoredSession(resumeKey)
          }
        } else if (statusResp.status === 404) {
          this.removeStoredSession(resumeKey)
        }
      } catch {
        // 네트워크 오류 시 기존 세션 유지 (다음 시도에 재개)
      }
    }

    // 2) 세션 없으면 새로 생성 (다중 업로드 배치 지원)
    if (!sessionId) {
      // 서버에 이미 활성 세션이 있는지 확인 (동시 업로드 제한으로 409가 날 수 있음)
      const activeResp = await fetch(`${this.baseUrl}/uploads/session`, { headers: authHeaders })
      if (activeResp.ok) {
        const active = await activeResp.json()
        if (active?.active && active?.session_id) {
          sessionId = active.session_id
          offset = active.received || 0
          this.storeSession(resumeKey, String(sessionId))
        }
      }
      if (!sessionId) {
        // 항상 새로운 배치 생성 (업로드 시행 단위로 히스토리 독립성 보장)
        let batchId: number | null = null
        try {
          const batchResp = await fetch(`${this.baseUrl}/uploads/batch`, {
            method: 'POST',
            headers: authHeaders,
            body: JSON.stringify({
              project_id: projectId,
              node_id: nodeId,
              preprocess_face: facePref,
              preprocess_plate: platePref,
            })
          })
          if (batchResp.ok) {
            const b = await batchResp.json()
            batchId = Number(b.batch_id)
          }
        } catch {}
        const createResp = await fetch(`${this.baseUrl}/uploads/session`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({
            project_id: projectId,
            node_id: nodeId,
            filename: file.name,
            total_size: file.size,
            batch_id: batchId,
            preprocess_face: facePref,
            preprocess_plate: platePref,
          })
        })
        if (createResp.status === 409) {
          // 방금 다른 탭/재시도에서 잡았을 수 있음. 한 번 더 active 조회 후 없으면 에러
          const again = await fetch(`${this.baseUrl}/uploads/session`, { headers: authHeaders })
          if (again.ok) {
            const active2 = await again.json()
            if (active2?.active && active2?.session_id) {
              sessionId = active2.session_id
              offset = active2.received || 0
              this.storeSession(resumeKey, String(sessionId))
            }
          }
          if (!sessionId) throw new Error(`세션 생성 실패: 409`)
        } else if (!createResp.ok) {
          throw new Error(`세션 생성 실패: ${createResp.status}`)
        } else {
          const session = await createResp.json()
          sessionId = session.session_id
          offset = 0
          this.storeSession(resumeKey, session.session_id)
        }
      }
    }

    // 3) 청크 업로드 (기본 8MB)
    if (!sessionId) {
      throw new Error('세션 ID가 없습니다. 업로드를 다시 시작해주세요.')
    }
    // 서버 권장 청크 크기를 캐싱하여 사용 (없으면 config 기본값)
    if (this.recommendedChunkSize === null) {
      try {
        const resp = await fetch(`${this.baseUrl}/uploads/config`, { headers: this.getAuthHeaders() })
        if (resp.ok) {
          const cfg = await resp.json()
          this.recommendedChunkSize = Number(cfg.recommended_chunk_size) || config.CHUNK_SIZE
        } else {
          this.recommendedChunkSize = config.CHUNK_SIZE
        }
      } catch {
        this.recommendedChunkSize = config.CHUNK_SIZE
      }
    }
    // 활성 업로드 상태 기록 (취소 지원)
    this.activeUpload = { sessionId, resumeKey, abortController: null, aborted: false }
    // 모달 즉시 표시 (activeUpload 설정 이후)
    try { window.dispatchEvent(new CustomEvent('upload:started')) } catch {}
    // 고정 청크 크기 사용(환경값/서버 권장) → 요청 수 최소화
    const chunkSize = (this.recommendedChunkSize || config.CHUNK_SIZE)
    while (offset < file.size) {
      if (this.activeUpload.aborted || localStorage.getItem(this.ABORT_KEY) === '1') {
        throw new Error('UPLOAD_ABORTED')
      }
      const end = Math.min(offset + chunkSize, file.size)
      const blob = file.slice(offset, end)
      const arrayBuf = await blob.arrayBuffer()
      const contentRange = `bytes ${offset}-${end - 1}/${file.size}`

      const controller = new AbortController()
      this.activeUpload.abortController = controller
      // 낙관적 진행률 업데이트: 서버 응답 전에도 화면에 즉시 반영되도록 함
      try {
        const optimistic = Math.max(0, Math.min(99.9, Math.round(((end / file.size) * 100) * 10) / 10))
        if (onProgress) onProgress(optimistic as any)
        this.emitProgressUpdate(optimistic as any, file.name)
      } catch {}
      let resp: Response
      try {
        resp = await fetch(`${this.baseUrl}/uploads/session/${sessionId}`, {
          method: 'PATCH',
          headers: {
            'Authorization': (authHeaders as any)['Authorization'] as string,
            'Content-Range': contentRange
          },
          body: arrayBuf,
          signal: controller.signal
        })
      } catch (e) {
        if (this.activeUpload.aborted) {
          throw new Error('UPLOAD_ABORTED')
        }
        throw e
      }
      if (!resp.ok) throw new Error(`청크 업로드 실패: ${resp.status}`)

      const status = await resp.json()
      offset = status.received
      // 소수 첫째 자리까지 진행률 표시 (대용량 파일에서 0%만 보이는 문제 완화)
      const percentFloat = Math.max(0, Math.min(100, Math.round(((offset / file.size) * 100) * 10) / 10))
      if (onProgress) onProgress(percentFloat)
      this.emitProgressUpdate(percentFloat as any, file.name)
    }

    // 4) 완료 호출
    const completeResp = await fetch(`${this.baseUrl}/uploads/session/${sessionId}/complete`, {
      method: 'POST',
      headers: authHeaders
    })
    if (!completeResp.ok) throw new Error(`완료 처리 실패: ${completeResp.status}`)
    const result = await completeResp.json()
    try {
      // 업로드 완료 후 히스토리 자동 새로고침을 위한 약식 이벤트
      const event = new CustomEvent('upload:completed')
      window.dispatchEvent(event)
    } catch {}
    // UI 동기화: 완료 직전/직후 진행률 100% 보장
    if (onProgress) onProgress(100)
    this.emitProgressUpdate(100, file.name)
    await new Promise((r) => setTimeout(r, 200))
    // 완료 시 세션 제거
    this.removeStoredSession(resumeKey)
    this.activeUpload = { sessionId: null, resumeKey: null, abortController: null, aborted: false }
    localStorage.removeItem(this.ABORT_KEY)

    // 배치 로컬 키 사용 안 함: 항상 새로운 배치로 동작
    return result
  }

  // === 이어받기 유틸 ===
  private buildResumeKey(file: File, nodeId: number, projectId: number): string {
    return `${projectId}:${nodeId}:${file.name}:${file.size}:${(file as any).lastModified ?? 0}`
  }

  private parseResumeKey(key: string): { projectId: number; nodeId: number; fileName: string; fileSize: number; lastModified: number } | null {
    const parts = key.split(':')
    if (parts.length < 5) return null
    const [p, n, name, size, lm] = parts
    const projectId = Number(p)
    const nodeId = Number(n)
    const fileSize = Number(size)
    const lastModified = Number(lm)
    if (Number.isNaN(projectId) || Number.isNaN(nodeId)) return null
    return { projectId, nodeId, fileName: name, fileSize, lastModified }
  }

  private getStoredSession(key: string): { session_id: string; ts?: number } | null {
    try {
      const raw = localStorage.getItem(this.LS_KEY)
      if (!raw) return null
      const map = JSON.parse(raw) as Record<string, { session_id: string; ts?: number }>
      return map[key] ?? null
    } catch {
      return null
    }
  }

  private storeSession(key: string, sessionId: string) {
    try {
      const raw = localStorage.getItem(this.LS_KEY)
      const map = raw ? JSON.parse(raw) : {}
      map[key] = { session_id: sessionId, ts: Date.now() }
      localStorage.setItem(this.LS_KEY, JSON.stringify(map))
    } catch {}
  }

  private removeStoredSession(key: string) {
    try {
      const raw = localStorage.getItem(this.LS_KEY)
      if (!raw) return
      const map = JSON.parse(raw)
      if (map[key]) {
        delete map[key]
        localStorage.setItem(this.LS_KEY, JSON.stringify(map))
      }
    } catch {}
  }

  // 업로드 취소 (현재 진행 중인 업로드)
  async cancelCurrentUpload() {
    // 전역 취소 플래그 설정 (즉시 루프 중단 신호)
    localStorage.setItem(this.ABORT_KEY, '1')

    if (this.activeUpload.sessionId) {
      this.activeUpload.aborted = true
      try { this.activeUpload.abortController?.abort() } catch {}
      try { await this.abortUploadSession(this.activeUpload.sessionId, this.activeUpload.resumeKey || undefined) } catch {}
      this.activeUpload = { sessionId: null, resumeKey: null, abortController: null, aborted: false }
    } else {
      // 활성 업로드가 없을 수도 있음(다른 탭에서 시작). 서버 보류 세션들을 정리
      try {
        const pending = await this.listPendingSessions()
        for (const p of pending) {
          try { await this.abortUploadSession(p.session_id, p.key) } catch {}
        }
      } catch {}
    }

    // 배치 업로드 진행 상태 정리
    try {
      // 취소 시에도 전체 배치 취소 의도로 판단하여 배치 키 제거
      localStorage.removeItem('upload_batch_total')
      localStorage.removeItem('upload_batch_index')
      const projectId = Number((window as any).currentProjectId || 0)
      const nodeId = Number((window as any).currentNodeId || 0)
      if (projectId && nodeId) {
        try { localStorage.removeItem(`batch:${projectId}:${nodeId}`) } catch {}
      }
    } catch {}

    localStorage.removeItem(this.ABORT_KEY)
  }

  // 브라우저 종료/새로고침 시 즉시 중단 신호를 보내기 위한 경량 호출
  async cancelCurrentUploadOnUnload() {
    try {
      localStorage.setItem(this.ABORT_KEY, '1')
      const sessionId = this.activeUpload.sessionId
      const resumeKey = this.activeUpload.resumeKey
      if (sessionId) {
        // keepalive를 사용하여 언로드 중에도 요청을 전송
        const headers = this.getAuthHeaders()
        try {
          await fetch(`${this.baseUrl}/uploads/session/${sessionId}/abort`, {
            method: 'POST',
            headers,
            keepalive: true,
            // 빈 body로 전송 (keepalive 제약 회피)
            body: '' as any
          })
        } catch {}
      }
      // 로컬 세션도 정리
      if (resumeKey) {
        this.removeStoredSession(resumeKey)
      }
      // 배치 키 제거
      try {
        localStorage.removeItem('upload_batch_total')
        localStorage.removeItem('upload_batch_index')
      } catch {}
    } catch {}
  }

  // 현재 활성 업로드 세션 ID 조회 (없으면 null)
  getActiveSessionId(): string | null {
    return this.activeUpload.sessionId
  }

  // 파일 삭제 (나중에 구현)
  async deleteFile(fileId: number): Promise<void> {
    // TODO: 파일 삭제 API 구현 후 연결
    throw new Error('파일 삭제 기능은 아직 구현되지 않았습니다.')
  }

  // 노드 생성 (나중에 구현)
  async createNode(parentId: number, name: string, projectId: number, nodeType: string = 'GROUP'): Promise<ProjectNode> {
    // TODO: 노드 생성 API 구현 후 연결
    throw new Error('노드 생성 기능은 아직 구현되지 않았습니다.')
  }

  // 노드 삭제 (나중에 구현)
  async deleteNode(nodeId: number, projectId: number): Promise<void> {
    // TODO: 노드 삭제 API 구현 후 연결
    throw new Error('노드 삭제 기능은 아직 구현되지 않았습니다.')
  }

  // ── 검색 ──────────────────────────────────────────────────────────────────

  async searchFiles(params: {
    q?: string
    dateFrom?: string
    dateTo?: string
    nodeId?: number
    uploadMethod?: 'manual' | 'auto'
    skip?: number
    limit?: number
  }): Promise<FileSearchResult[]> {
    const p = new URLSearchParams()
    if (params.q) p.set('q', params.q)
    if (params.dateFrom) p.set('date_from', params.dateFrom)
    if (params.dateTo) p.set('date_to', params.dateTo)
    if (params.nodeId != null) p.set('node_id', String(params.nodeId))
    if (params.uploadMethod) p.set('upload_method', params.uploadMethod)
    if (params.skip != null) p.set('skip', String(params.skip))
    if (params.limit != null) p.set('limit', String(params.limit))

    const res = await fetch(`${this.baseUrl}/files/search?${p}`, { headers: this.getAuthHeaders() })
    if (!res.ok) throw new Error('검색 실패')
    return res.json()
  }

  async searchFilesCount(params: {
    q?: string
    dateFrom?: string
    dateTo?: string
    nodeId?: number
    uploadMethod?: 'manual' | 'auto'
  }): Promise<number> {
    const p = new URLSearchParams()
    if (params.q) p.set('q', params.q)
    if (params.dateFrom) p.set('date_from', params.dateFrom)
    if (params.dateTo) p.set('date_to', params.dateTo)
    if (params.nodeId != null) p.set('node_id', String(params.nodeId))
    if (params.uploadMethod) p.set('upload_method', params.uploadMethod)

    const res = await fetch(`${this.baseUrl}/files/search/count?${p}`, { headers: this.getAuthHeaders() })
    if (!res.ok) throw new Error('카운트 조회 실패')
    const data = await res.json()
    return data.count
  }

  // 검색 다운로드 URL 생성 (access_token 쿼리 포함 → 브라우저 네이티브 다운로드용)
  buildSearchDownloadUrl(params: {
    q?: string
    dateFrom?: string
    dateTo?: string
    nodeId?: number
    uploadMethod?: 'manual' | 'auto'
  }): string {
    const p = new URLSearchParams()
    if (params.q) p.set('q', params.q)
    if (params.dateFrom) p.set('date_from', params.dateFrom)
    if (params.dateTo) p.set('date_to', params.dateTo)
    if (params.nodeId != null) p.set('node_id', String(params.nodeId))
    if (params.uploadMethod) p.set('upload_method', params.uploadMethod)
    const token = localStorage.getItem('access_token')
    if (token) p.set('access_token', token)
    return `${this.baseUrl}/files/search/download?${p}`
  }

  // 상태만 판별(200=준비됨 / 202=패키징 중). 실제 파일은 네이티브 다운로드로 받으므로 본문은 소비하지 않는다.
  async requestSearchDownload(params: {
    q?: string
    dateFrom?: string
    dateTo?: string
    nodeId?: number
    uploadMethod?: 'manual' | 'auto'
  }): Promise<{ status: 'stream' | 'processing'; url: string; pollingUrl?: string; exportPath?: string }> {
    const url = this.buildSearchDownloadUrl(params)
    // 준비 여부만 확인한다. 예전에는 확인용으로 실제 전송을 열고 body.cancel() 로
    // 끊었는데, 서버는 이미 파일을 열고 첫 바이트를 보낸 뒤였다 — 그걸 버리고
    // 처음부터 다시 받으니 NAS 읽기가 두 번 시작됐다.
    const probeUrl = url + (url.includes('?') ? '&' : '?') + 'prepare_only=true'
    const res = await fetch(probeUrl, { method: 'GET', headers: this.getAuthHeaders() })

    // 서버가 스냅샷 키를 주면 다운로드 URL에 박는다 — 패키징 사이에 자동수집으로
    // 결과 집합이 변해도 "요청 시점의 산출물"을 정확히 받기 위함.
    const withSnapshot = (u: string, snap?: string) =>
      snap ? u + (u.includes('?') ? '&' : '?') + `snapshot=${snap}` : u

    if (res.status === 200) {
      let snap: string | undefined
      try { snap = (await res.json())?.snapshot } catch {}
      return { status: 'stream', url: withSnapshot(url, snap) }
    }
    if (res.status === 202) {
      const data = await res.json()
      return { status: 'processing', url: withSnapshot(url, data.snapshot), pollingUrl: data.polling_url, exportPath: data.export_path }
    }
    throw new Error('다운로드 요청 실패')
  }

  async checkDownloadStatus(pollingUrl: string): Promise<{ status: 'ready' | 'processing' | 'failed'; message?: string; progress?: number }> {
    const url = pollingUrl.startsWith('http') ? pollingUrl : `${this.baseUrl}${pollingUrl}`
    const res = await fetch(url, { headers: this.getAuthHeaders() })
    if (!res.ok) return { status: 'processing' }
    const data = await res.json()
    const status = data.status === 'ready' ? 'ready' : data.status === 'failed' ? 'failed' : 'processing'
    return { status, message: data.message, progress: data.progress }
  }
}

export interface FileSearchResult {
  id: number
  node_id: number
  filename: string
  original_filename?: string
  file_type: string
  file_size: number
  created_at: string
  thumbnail_url?: string
  upload_method: string
  preprocess_state?: string | null
  sub_type?: string | null
  metadata?: Record<string, any> | null
  node_path: string
}

export default new RawDataService()